import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';

import { isFalsy, isTruthy } from 'lib/Helpers';
import {
  checkNameUniqueness as checkUniqueness,
  initializeDuplicateModal as initializeModal,
  resetDuplicateModal as resetModal,
  updateDuplicateName as updateName,
} from 'modules/DuplicateModal';

import Button from 'BridgReusables/Button';
import Input from 'BridgReusables/Input';
import Modal from 'BridgReusables/Modals';
import * as styles from './DuplicateModal.scss';

/**
 * @class DuplicateModal
 * @classdesc Reusable DuplicateModal Component
 * @property {Function} handleNameUniqueness
 */
class DuplicateModal extends Component {
  static propTypes = {
    checkNameUniqueness: PropTypes.func.isRequired,
    currentName: PropTypes.string.isRequired,
    getErrorMessage: PropTypes.func.isRequired,
    handleCancel: PropTypes.func.isRequired,
    handleDuplicate: PropTypes.func.isRequired,
    initializeDuplicateModal: PropTypes.func.isRequired,
    inputLabel: PropTypes.string.isRequired,
    isNameUnique: PropTypes.bool.isRequired,
    isSuggestedNameLoading: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    nameValid: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    resetDuplicateModal: PropTypes.func.isRequired,
    suggestedDataKey: PropTypes.string.isRequired,
    suggestedUrl: PropTypes.string.isRequired,
    updateDuplicateName: PropTypes.func.isRequired,
    uniquenessDataKey: PropTypes.string.isRequired,
    uniquenessUrl: PropTypes.string.isRequired,
    checkDuplicateName: PropTypes.func,
    maxLength: PropTypes.number,
    showMaxLengthLabel: PropTypes.bool,
    suggestedQueryKey: PropTypes.string,
    suggestedRequestType: PropTypes.oneOf([
      'GET',
      'POST',
    ]),
    title: PropTypes.string,
    uniquenessQueryKey: PropTypes.string,
    uniquenessRequestType: PropTypes.oneOf([
      'GET',
      'POST',
    ]),
  };

  static defaultProps = {
    checkDuplicateName: () => true,
    maxLength: null,
    showMaxLengthLabel: false,
    suggestedQueryKey: '',
    suggestedRequestType: 'POST',
    title: null,
    uniquenessQueryKey: '',
    uniquenessRequestType: 'POST',
  };

  constructor(props) {
    super(props);
    this.handleNameUniqueness = debounce(this.handleNameUniqueness, 250);
  }

  componentDidUpdate(prevProps) {
    const { currentName, name, open } = this.props;
    const { currentName: oldCurrent } = prevProps;
    /* Initializes the Modal when open, currentName has just been passed, and we haven't already fetched a suggested name */
    if (isFalsy(oldCurrent) && isTruthy(currentName) && isTruthy(open) && isFalsy(name)) return this.initialize();
    return null;
  }

  initialize = () => {
    const {
      currentName,
      initializeDuplicateModal,
      suggestedDataKey,
      suggestedQueryKey,
      suggestedRequestType,
      suggestedUrl,
      uniquenessDataKey,
      uniquenessQueryKey,
      uniquenessRequestType,
      uniquenessUrl,
    } = this.props;

    return initializeDuplicateModal({
      currentName,
      suggestedDataKey,
      suggestedQueryKey,
      suggestedRequestType,
      suggestedUrl,
      uniquenessDataKey,
      uniquenessQueryKey,
      uniquenessRequestType,
      uniquenessUrl,
    });
  };

  handleCancel = () => {
    const { handleCancel, resetDuplicateModal } = this.props;

    handleCancel();
    return resetDuplicateModal();
  };

  /**
   * Provides Final Duplicate Name for Duplicate Callback
   * @return {Function} props.handleDuplicate
   */
  handleDuplicate = () => {
    const { handleDuplicate, name, resetDuplicateModal } = this.props;

    handleDuplicate(name);
    return resetDuplicateModal();
  };

  /**
   * Handles Disabling Primary CTA in Error States
   * @return {boolean}
   */
  handleDuplicateDisabled = () => {
    const { isNameUnique, name, nameValid } = this.props;
    /**
     * isFalsy(name) => Checks if Name is Empty
     * isFalsy(isNameUnique) => Checks if Name Has Been Marked as Not Unique
     * isFalsy(nameValid) => Checks if Name Has Failed Additional Validation Criteria from `props.checkDuplicateName`
     */
    return (isFalsy(name) || isFalsy(isNameUnique) || isFalsy(nameValid));
  };

  /**
   * onChange Handler for Duplicate Name Input
   * @param {Object}
   * @return {Function} handleNameUniqueness
   */
  handleNameInput = ({ target: { value } }) => {
    const { checkDuplicateName, updateDuplicateName } = this.props;

    updateDuplicateName(value, checkDuplicateName);
    return this.handleNameUniqueness();
  };

  /**
   * Debounced Handler to call `checkNameUniqueness`
   * @return {Function} checkNameUniqueness
   */
  handleNameUniqueness = () => {
    const { checkNameUniqueness } = this.props;
    return checkNameUniqueness();
  };

  render() {
    const {
      getErrorMessage,
      inputLabel,
      isNameUnique,
      isSuggestedNameLoading,
      maxLength,
      name,
      nameValid,
      onClose,
      open,
      showMaxLengthLabel,
      title,
    } = this.props;

    if (isFalsy(open)) return null;
    return (
      <Modal onClose={onClose} open={open}>
        <div className={styles.duplicateModal}>
          <h3 className={styles.modalTitle}>{title}</h3>
          <Input
            className={styles.nameInput}
            disabled={isSuggestedNameLoading}
            divClass={styles.nameInputContainer}
            error={getErrorMessage({ isNameUnique, nameValid, name })}
            hasError={isFalsy(isNameUnique) || isFalsy(nameValid)}
            label={inputLabel}
            maxLength={maxLength}
            onChange={this.handleNameInput}
            showMaxLengthLabel={showMaxLengthLabel}
            size="LARGE"
            value={name}
          />
          <div className={styles.buttons}>
            <Button onClick={this.handleCancel} variant="outlined">Cancel</Button>
            <Button onClick={this.handleDuplicate} disabled={this.handleDuplicateDisabled()} style={{ marginLeft: '16px' }}>Duplicate</Button>
          </div>
        </div>
      </Modal>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  checkNameUniqueness: () => dispatch(checkUniqueness()),
  initializeDuplicateModal: data => dispatch(initializeModal(data)),
  resetDuplicateModal: () => dispatch(resetModal()),
  updateDuplicateName: (duplicateName, validationFn) => dispatch(updateName(duplicateName, validationFn)),
});

const mapStateToProps = state => ({
  name: state.duplicateModal.duplicateName,
  nameValid: state.duplicateModal.duplicateNameValid,
  isNameUnique: state.duplicateModal.isDuplicateNameUnique,
  isSuggestedNameLoading: state.duplicateModal.isSuggestedNameLoading,
});

const ConnectedDuplicateModal = connect(mapStateToProps, mapDispatchToProps)(DuplicateModal);

export { DuplicateModal as PureDuplicateModal };
export default ConnectedDuplicateModal;
